// src/pages/Appli/AppliAdmin/VisitorCenter/VisitorCenterPage.jsx

import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  FiClock,
  FiMessageCircle,
  FiPhoneCall,
  FiUsers,
} from "react-icons/fi";

import VisitorCenterHeader from "./components/VisitorCenterHeader";
import VisitorAnalyticsPanel from "./components/VisitorAnalyticsPanel";
import LiveVisitorsPanel from "./components/LiveVisitorsPanel";
import VisitorDetailsPanel from "./components/VisitorDetailsPanel";
import SessionsPanel from "./components/SessionsPanel";
import ConversationsPanel from "./components/ConversationsPanel";
import CallbackRequestsPanel from "./components/CallbackRequestsPanel";

import useVisitorAnalytics from "./hooks/useVisitorAnalytics";
import useLiveVisitors from "./hooks/useLiveVisitors";
import useVisitorSessions from "./hooks/useVisitorSessions";
import useVisitorConversations from "./hooks/useVisitorConversations";
import useVisitorCallbacks from "./hooks/useVisitorCallbacks";

import {
  useVisitorAdminAlertsContext,
} from "./context/VisitorAdminAlertsContext";

import {
  VISITOR_CENTER_TABS,
} from "./data/visitorCenterData";

import {
  SectionCard,
} from "./ui/VisitorCenterUi";

const TAB_STORAGE_KEY =
  "monappliclub_visitor_center_active_tab";

const VALID_TABS =
  VISITOR_CENTER_TABS.map(
    (tab) => tab.key,
  );

function KpiCard({
  icon: Icon,
  label,
  value,
  description,
  active = false,
  alert = false,
  onClick,
}) {
  const numericValue =
    Number(value) || 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-[24px] border p-5 text-left shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md ${
        active
          ? "border-[#2EA7E0] bg-[#EEF7FC]"
          : alert &&
              numericValue > 0
            ? "border-red-200 bg-red-50/40"
            : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div
            className={`text-sm font-bold ${
              alert &&
              numericValue > 0
                ? "text-red-600"
                : "text-slate-500"
            }`}
          >
            {label}
          </div>

          <div
            className={`mt-2 text-3xl font-extrabold ${
              alert &&
              numericValue > 0
                ? "text-red-600"
                : "text-[#123B57]"
            }`}
          >
            {numericValue}
          </div>

          <div className="mt-2 text-xs leading-5 text-slate-400">
            {description}
          </div>
        </div>

        <div
          className={`relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-xl ${
            alert &&
            numericValue > 0
              ? "bg-red-100 text-red-600"
              : active
                ? "bg-[#123B57] text-white"
                : "bg-[#EEF7FC] text-[#2EA7E0]"
          }`}
        >
          <Icon />

          {alert &&
            numericValue > 0 && (
              <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full border-2 border-white bg-red-500" />
            )}
        </div>
      </div>
    </button>
  );
}

export default function VisitorCenterPage() {
  const [
    activeTab,
    setActiveTab,
  ] = useState(() => {
    const storedTab =
      localStorage.getItem(
        TAB_STORAGE_KEY,
      );

    return VALID_TABS.includes(
      storedTab,
    )
      ? storedTab
      : "overview";
  });

  const {
    summary,
    loading:
      alertsLoading,

    errorMessage:
      alertsErrorMessage,

    refresh:
      refreshAlerts,
  } =
    useVisitorAdminAlertsContext();

  const analytics =
    useVisitorAnalytics({
      enabled:
        activeTab ===
        "overview",
    });

  const live =
    useLiveVisitors({
      enabled:
        activeTab === "live",

      pollMs: 8000,
    });

  const sessions =
    useVisitorSessions({
      enabled:
        activeTab ===
        "sessions",
    });

  const conversations =
    useVisitorConversations({
      enabled:
        activeTab ===
        "conversations",

      pollMs: 10000,
    });

  const callbacks =
    useVisitorCallbacks({
      enabled:
        activeTab ===
        "callbacks",
    });

  useEffect(() => {
    localStorage.setItem(
      TAB_STORAGE_KEY,
      activeTab,
    );
  }, [activeTab]);

  const handleTabChange =
    useCallback(
      (nextTab) => {
        if (
          !VALID_TABS.includes(
            nextTab,
          )
        ) {
          return;
        }

        setActiveTab(
          nextTab,
        );
      },
      [],
    );

  const handleRefresh =
    useCallback(async () => {
      await refreshAlerts({
        notify: false,
      });

      if (
        activeTab ===
        "overview"
      ) {
        return analytics.refresh();
      }

      if (
        activeTab === "live"
      ) {
        return live.refresh();
      }

      if (
        activeTab ===
        "sessions"
      ) {
        return sessions.refresh();
      }

      if (
        activeTab ===
        "conversations"
      ) {
        return conversations.refresh();
      }

      if (
        activeTab ===
        "callbacks"
      ) {
        return callbacks.refresh();
      }

      return null;
    }, [
      activeTab,
      refreshAlerts,
      analytics,
      live,
      sessions,
      conversations,
      callbacks,
    ]);

  const activeTabLoading =
    activeTab === "overview"
      ? analytics.loading
      : activeTab === "live"
        ? live.loading
        : activeTab ===
            "sessions"
          ? sessions.loading
          : activeTab ===
              "conversations"
            ? conversations.loading
            : activeTab ===
                "callbacks"
              ? callbacks.loading
              : false;

  const refreshing =
    alertsLoading ||
    activeTabLoading;

  return (
    <div className="space-y-6 pb-10">
      <VisitorCenterHeader
        activeTab={activeTab}
        onTabChange={
          handleTabChange
        }
        onRefresh={
          handleRefresh
        }
        refreshing={
          refreshing
        }
      />

      {alertsErrorMessage && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {
            alertsErrorMessage
          }
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          icon={FiUsers}
          label="En direct"
          value={
            summary.live_visitors
          }
          description="Visiteurs actuellement actifs ou temporairement absents."
          active={
            activeTab === "live"
          }
          onClick={() =>
            handleTabChange(
              "live",
            )
          }
        />

        <KpiCard
          icon={FiClock}
          label="Sessions aujourd’hui"
          value={
            summary.today_sessions
          }
          description="Nouvelles sessions enregistrées depuis minuit."
          active={
            activeTab ===
            "sessions"
          }
          onClick={() =>
            handleTabChange(
              "sessions",
            )
          }
        />

        <KpiCard
          icon={
            FiMessageCircle
          }
          label="Conversations non lues"
          value={
            summary.unread_conversations
          }
          description={`${Number(
            summary.unread_messages,
          ) || 0} message(s) non lu(s) à traiter.`}
          active={
            activeTab ===
            "conversations"
          }
          alert
          onClick={() =>
            handleTabChange(
              "conversations",
            )
          }
        />

        <KpiCard
          icon={FiPhoneCall}
          label="Demandes à traiter"
          value={
            summary.new_callback_requests
          }
          description="Demandes de rappel ou de démonstration au statut Nouvelle."
          active={
            activeTab ===
            "callbacks"
          }
          alert
          onClick={() =>
            handleTabChange(
              "callbacks",
            )
          }
        />
      </div>

      {activeTab ===
        "overview" && (
        <VisitorAnalyticsPanel
          data={analytics.data}
          filters={
            analytics.filters
          }
          loading={
            analytics.loading
          }
          errorMessage={
            analytics.errorMessage
          }
          onRangeChange={
            analytics.setRange
          }
          onFilterChange={
            analytics.updateFilter
          }
          onRefresh={
            analytics.refresh
          }
        />
      )}

      {activeTab ===
        "live" && (
        <div className="grid gap-6 2xl:grid-cols-[380px_minmax(0,1fr)]">
          <LiveVisitorsPanel
            visitors={
              live.visitors
            }
            selectedVisitorId={
              live.selectedVisitorId
            }
            onSelectVisitor={
              live.selectVisitor
            }
            loading={
              live.loading
            }
            errorMessage={
              live.errorMessage
            }
          />

          <SectionCard
            title="Parcours du visiteur"
            subtitle="Pages actuellement consultées et historique de navigation pendant la session."
          >
            <VisitorDetailsPanel
              visitor={
                live.selectedVisitor
              }
              loading={
                live.detailsLoading
              }
            />
          </SectionCard>
        </div>
      )}

      {activeTab ===
        "sessions" && (
        <SessionsPanel
          sessions={
            sessions.rows
          }
          filters={
            sessions.filters
          }
          page={
            sessions.page
          }
          totalPages={
            sessions.totalPages
          }
          count={
            sessions.count
          }
          loading={
            sessions.loading
          }
          errorMessage={
            sessions.errorMessage
          }
          selectedSession={
            sessions.selectedSession
          }
          detailsLoading={
            sessions.detailsLoading
          }
          onFilterChange={
            sessions.updateFilter
          }
          onPageChange={
            sessions.setPage
          }
          onSelectSession={
            sessions.selectSession
          }
          detailsComponent={
            <VisitorDetailsPanel />
          }
        />
      )}

      {activeTab ===
        "conversations" && (
        <ConversationsPanel
          conversations={
            conversations.rows
          }
          selectedConversationId={
            conversations.selectedConversationId
          }
          selectedConversation={
            conversations.selectedConversation
          }
          status={
            conversations.status
          }
          onlyUnread={
            conversations.onlyUnread
          }
          page={
            conversations.page
          }
          totalPages={
            conversations.totalPages
          }
          loading={
            conversations.loading
          }
          detailsLoading={
            conversations.detailsLoading
          }
          sending={
            conversations.sending
          }
          errorMessage={
            conversations.errorMessage
          }
          onStatusChange={(
            value,
          ) => {
            conversations.setStatus(
              value,
            );

            conversations.setPage(
              1,
            );
          }}
          onOnlyUnreadChange={(
            value,
          ) => {
            conversations.setOnlyUnread(
              value,
            );

            conversations.setPage(
              1,
            );
          }}
          onPageChange={
            conversations.setPage
          }
          onSelectConversation={
            conversations.selectConversation
          }
          onSendMessage={
            conversations.sendMessage
          }
          onConversationStatusChange={
            conversations.changeConversationStatus
          }
        />
      )}

      {activeTab ===
        "callbacks" && (
        <CallbackRequestsPanel
          requests={
            callbacks.rows
          }
          selectedRequest={
            callbacks.selectedRequest
          }
          q={callbacks.q}
          status={
            callbacks.status
          }
          page={
            callbacks.page
          }
          totalPages={
            callbacks.totalPages
          }
          loading={
            callbacks.loading
          }
          detailsLoading={
            callbacks.detailsLoading
          }
          errorMessage={
            callbacks.errorMessage
          }
          onQueryChange={(
            value,
          ) => {
            callbacks.setQ(
              value,
            );

            callbacks.setPage(
              1,
            );
          }}
          onStatusChange={(
            value,
          ) => {
            callbacks.setStatus(
              value,
            );

            callbacks.setPage(
              1,
            );
          }}
          onPageChange={
            callbacks.setPage
          }
          onSelectRequest={
            callbacks.selectRequest
          }
          onChangeRequestStatus={
            callbacks.changeStatus
          }
          onDeleteRequest={
            callbacks.removeRequest
          }
        />
      )}
    </div>
  );
}